import React, { Component } from "react";
import { withRouter } from "react-router";
import { Card, Button, Form, Checkbox, Input } from "antd";
import DateRangePicker from "../common/date-range-picker";
import MultiSelect from "../common/multi-select";
import { getDistinctValues } from "../../services/api-maxval";
import { fromExactMatchQueryString, toExactMatchQueryString } from "../../helpers/url";
import { isArray } from "lodash";

const ENTITY_TYPES = ["NPE (Patent Assertion Entity)", "NPE (Small Company)", "NPE (Individual)", "Operating Company", "University", "Gov/NGO/Non-Profit"];
const INDUSTRIES = ["T", "M", "O"];
const INDUSTRY_MAP = {
  T: "High-Tech (T)",
  M: "Medical (M)",
  O: "Other (O)"
};
const SOURCE_OPTIONS = [
  { label: "District Court", value: "DC" },
  { label: "Supreme Court", value: "SC" },
  { label: "Federal Circuit", value: "CAFC" },
  { label: "Federal Claims", value: "CFC" },
  { label: "International Trade Commission", value: "ITC" }
];
const SME_ANNOTATION_OPTIONS_MAP = {
  Loc: "Large Operating Company",
  Sme: "SME",
  NPE: "NPE",
  G: "Government",
  U: "University",
  I: "Individual",
  O: "Other"
};

const propsToStates = q => ({
  // convert url query to states
  inputCaseNumber: q.case_no,
  inputPlaintiff: q.plaintiff,
  inputDefendant: q.defendant,
  inputStatus: q.status,
  inputEntityType: q.entity_type,
  inputIndustry: fromExactMatchQueryString(q.industry),
  inputPatent: q.patents,
  inputJudge: q.judge,
  inputFiledDate: q.filed_date,
  inputClosedDate: q.closed_date,
  inputSources: q.flag && !isArray(q.flag) ? [q.flag] : q.flag, // inputSources should always be an array
  inputCourt: q.court,
  inputOutcomeType: q.cause_of_action,
  inputSmeAnnotation: fromExactMatchQueryString(q.sme_annotation),
  inputTPLF: q.has_tplf,
  inputNPEAgg: q.npe_aggregator,
  inputProduct: q.product
});

const stateKey2Field = {
  inputCaseNumber: "case_no",
  inputPlaintiff: "plaintiff",
  inputDefendant: "defendant",
  inputPatent: "patents",
  inputJudge: "judge",
  inputCourt: "court",
  inputOutcomeType: "cause_of_action",
  inputSmeAnnotation: "sme_annotation",
  inputNPEAgg: "npe_aggregator"
};

class Toolbar extends Component {
  state = propsToStates(this.props.location.query);

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(propsToStates(nextProps.location.query));
  }

  render() {
    return (
      <Card
        title="Litigation"
        extra={
          <Button size="small" onClick={this._onResetClick}>
            {"Reset"}
          </Button>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Source">
            <Checkbox.Group options={SOURCE_OPTIONS} value={this.state.inputSources || SOURCE_OPTIONS.map(opt => opt.value)} onChange={this._handleSelectChange("inputSources")} />
          </Form.Item>
          {this.renderSelectFormItem("inputCaseNumber", "Case", "e.g. 1:11-cv-00573")}
          {this.renderSelectFormItem("inputStatus", "Status", "e.g. Open", ["Open", "Closed"])}
          {this.renderSelectFormItem("inputCourt", "Court", "e.g. California Northern District Court")}
          {this.renderSelectFormItem("inputOutcomeType", "Cause of Action", "e.g. Infringement")}
          {this.renderSelectFormItem("inputPlaintiff", "Plaintiff", "e.g. Google")}
          {this.renderSelectFormItem("inputDefendant", "Defendant", "e.g. Apple")}
          {this.renderSelectFormItem("inputEntityType", "Plaintiff Entity Type", "e.g. NPE (Patent Assertion Entity)", ENTITY_TYPES)}
          {this.renderSelectFormItem("inputNPEAgg", "Plaintiff NPE Aggregator", "e.g. IP Edge")}
          {this.renderSelectFormItem("inputTPLF", "Third Party Financing", "e.g. true", ["true", "false"], undefined, "default")}
          {this.renderSelectFormItem("inputSmeAnnotation", "Defendant Entity Type", "e.g. SME", undefined, v => (v ? SME_ANNOTATION_OPTIONS_MAP[v] : v))}
          {this.renderSelectFormItem("inputPatent", "Patents in Case", "e.g. 8855637")}
          {this.renderSelectFormItem("inputIndustry", "Industry", "e.g. High-Tech (T)", INDUSTRIES, v => (v ? INDUSTRY_MAP[v] : v))}
          {this.renderSelectFormItem("inputJudge", "Judge", "e.g. Robert S. Lasnik")}
          {this.renderDateFormItem("inputFiledDate", "Filing Date")}
          {this.renderDateFormItem("inputClosedDate", "Termination Date")}
          <Form.Item label="Infringed Product">
            <Input value={this.state.inputProduct} placeholder="e.g. Smartphone" onChange={e => this.setState({ inputProduct: e.target.value })} onPressEnter={this._onSearchClick} />
          </Form.Item>
        </Form>
      </Card>
    );
  }

  renderSelectFormItem = (key, label, placeholder, options, valueRenderer) => (
    <Form.Item label={label}>
      <MultiSelect
        value={this.state[key]}
        onChange={this._handleSelectChange(key)}
        options={options}
        optionApiCall={options ? undefined : val => getDistinctValues(stateKey2Field[key], val, this._getSourceFlag())}
        optionRender={valueRenderer}
        placeholder={placeholder}
      />
    </Form.Item>
  );

  renderDateFormItem = (key, label) => (
    <Form.Item label={label}>
      <DateRangePicker value={this.state[key]} onChange={this._handleSelectChange(key)} allowClear={true} />
    </Form.Item>
  );

  _handleSelectChange = stateKey => e =>
    this.setState(
      {
        [stateKey]: e
      },
      this._onSearchClick
    );

  _getSourceFlag = () => {
    const { inputSources } = this.state;
    const flag = inputSources && inputSources.length > 0 && inputSources.length < SOURCE_OPTIONS.length ? inputSources : undefined; // search all fields by default
    return { flag };
  };

  _onSearchClick = () => {
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: {
        ...this._getSourceFlag(),
        case_no: this.state.inputCaseNumber,
        plaintiff: this.state.inputPlaintiff,
        defendant: this.state.inputDefendant,
        status: this.state.inputStatus,
        entity_type: this.state.inputEntityType,
        industry: toExactMatchQueryString(this.state.inputIndustry),
        patents: this.state.inputPatent,
        judge: this.state.inputJudge,
        filed_date: this.state.inputFiledDate,
        closed_date: this.state.inputClosedDate,
        court: this.state.inputCourt,
        cause_of_action: this.state.inputOutcomeType,
        sme_annotation: toExactMatchQueryString(this.state.inputSmeAnnotation),
        npe_aggregator: this.state.inputNPEAgg,
        has_tplf: this.state.inputTPLF,
        product: this.state.inputProduct,
        sort: this.props.location.query.sort ? this.props.location.query.sort : "-filed_date"
      }
    });
  };

  _onResetClick = () =>
    this.setState(
      {
        inputCaseNumber: undefined,
        inputPlaintiff: undefined,
        inputDefendant: undefined,
        inputStatus: undefined,
        inputEntityType: undefined,
        inputIndustry: undefined,
        inputPatent: undefined,
        inputJudge: undefined,
        inputFiledDate: undefined,
        inputClosedDate: undefined,
        inputSources: undefined,
        inputCourt: undefined,
        inputOutcomeType: undefined,
        inputSmeAnnotation: undefined,
        inputTPLF: undefined,
        inputNPEAgg: undefined,
        inputProduct: undefined
      },
      () => {
        this.props.router.push({
          pathname: this.props.location.pathname,
          query: {
            sort: this.props.location.query.sort
          }
        });
      }
    );
}

export default withRouter(Toolbar);
